import { PropsOf } from '@emotion/react';
import {
  ArrowBackIosNew as ArrowBack,
  RefreshRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAGE_PADDING } from '../../constants/ui';
import { important } from '../../utils/global/helper';

interface Props {
  containerClassName?: string;
  mainContainerProps?: PropsOf<typeof Stack>;
  containerProps?: PropsOf<typeof Box>;
  title?: string;
  titleAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  isLoading?: boolean;
  withoutChildContainer?: boolean;
  childContainerProps?: PropsOf<typeof Box>;
  headerIsHidden?: boolean;
  refresh?: () => void | Promise<void>;
  guard?: {
    isAllowed: boolean;
    redirectTo?: string;
  };
  topSection?: React.ReactNode;
  onBack?: () => void;
}

const HEADER_GAP = 1;

function ChildContainer({
  children,
  withoutChildContainer,
  childContainerProps,
}: {
  children: React.ReactNode;
  withoutChildContainer: boolean;
  childContainerProps?: PropsOf<typeof Box>;
}) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (withoutChildContainer) return <>{children}</>;
  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      gap={3}
      {...childContainerProps}>
      {children}
    </Box>
  );
}

function PageLayout({
  children,
  isLoading,
  title,
  titleAction,
  containerClassName,
  mainContainerProps,
  containerProps,
  childContainerProps,
  withoutChildContainer = false,
  headerIsHidden = false,
  refresh,
  guard,
  topSection,
  onBack,
}: PropsWithChildren<Props>) {
  const navigate = useNavigate();

  React.useEffect(() => {
    if (guard && !guard.isAllowed) {
      navigate(guard.redirectTo || '/');
    }
  }, [guard, navigate]);

  return (
    <Stack width="100%" {...(mainContainerProps ?? {})}>
      {topSection}
      <Box
        className={containerClassName}
        width="100%"
        display="flex"
        flexDirection="column"
        gap={3}
        p={PAGE_PADDING}
        boxSizing="border-box"
        {...containerProps}>
        {!headerIsHidden && (
          <Box width="100%" display="flex" alignItems="center" gap={HEADER_GAP}>
            <Stack
              color="text.primary"
              direction="row"
              alignItems="center"
              justifyContent="start"
              gap={HEADER_GAP}
              p={0}
              m={0}
              maxWidth="calc(100% - 40px)" // 40px is the width of the loading spinner & refresh button
              component={onBack ? Button : 'div'}
              onClick={onBack}
              sx={{
                ':hover': {
                  bgcolor: 'transparent',
                },
              }}>
              {/* Title */}
              {onBack && <ArrowBack />}
              {(title || isLoading) && (
                <Stack direction="row" alignItems="center" gap={HEADER_GAP}>
                  {!!title && (
                    <Typography noWrap variant="h1" color="inherit">
                      {title}
                    </Typography>
                  )}
                  {isLoading && <CircularProgress color="primary" size={20} />}
                </Stack>
              )}
            </Stack>

            {/* Actions */}
            {(!!titleAction || refresh) && (
              <Stack
                direction="row"
                alignItems="center"
                gap={HEADER_GAP}
                ml="auto">
                {refresh && !isLoading && (
                  <Button
                    variant="link"
                    onClick={refresh}
                    sx={{
                      minWidth: 0,
                      p: 0.5,
                    }}>
                    <RefreshRounded color="primary" width={20} />
                  </Button>
                )}
                {titleAction && (
                  <Button
                    variant="contained"
                    className="primary"
                    onClick={titleAction.onClick}
                    disabled={titleAction.disabled}
                    sx={{ minWidth: 0, boxShadow: important('none') }}>
                    {titleAction.label}
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        )}
        <ChildContainer
          withoutChildContainer={withoutChildContainer}
          childContainerProps={childContainerProps}>
          {children}
        </ChildContainer>
      </Box>
    </Stack>
  );
}

export default PageLayout;
